/**
 * Debug Mode Composable
 *
 * Provides reactive access to the debug URL parameter.
 * When `?debug=true` is present in the URL, debug mode is active.
 *
 * @example
 * ```typescript
 * const { isDebugMode } = useDebugMode();
 *
 * if (isDebugMode.value) {
 *   console.log('Debug mode is active');
 * }
 * ```
 */

import { computed } from 'vue';

const DEBUG_MODE_CACHED = new URLSearchParams(window.location.search).get('debug') === 'true';

/**
 * Composable for checking debug mode status
 *
 * @returns Object with isDebugMode computed ref and helper function
 */
export function useDebugMode() {
  /**
   * Reactive computed that checks if debug=true is in URL
   * Re-evaluates on each access (reads current window.location.search)
   */
  const isDebugMode = computed(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === 'true';
  });

  /**
   * Get the debug parameter value for URL building
   * @returns 'true' if debug mode is active, null otherwise
   */
  function getDebugParam(): string | null {
    return isDebugMode.value ? 'true' : null;
  }

  return {
    isDebugMode,
    getDebugParam,
  };
}

/**
 * Standalone function to check debug mode without composable
 * Useful in non-component contexts (services, utilities)
 * Uses cached value for performance (safe to call frequently)
 *
 * @returns true if debug=true is in current URL
 */
export function isDebugModeActive(): boolean {
  return DEBUG_MODE_CACHED;
}

/**
 * Get debug parameter from current URL for URL building
 * Useful in utility functions that build URLs
 *
 * @returns 'true' if debug mode is active, null otherwise
 */
export function getDebugParamFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const debugValue = params.get('debug');
  return debugValue === 'true' ? 'true' : null;
}
